import React, { useEffect, useRef, useState } from 'react'
import { Checks, Copy, UserPlus, X } from "@phosphor-icons/react";
import ManageRoomGuest from './ManageRoomGuest';
import ManageRoomGuestFields from './ManageRoomGuestFields';
import "./../../Styles/ManageGuestsModal.css";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { toast, Toaster } from "react-hot-toast";
import axiosInstance from '../../api/axiosInstance';
import { buildGuestPayload } from '../../api/guestPayload';
import { collectOtherRoomContacts } from '../../api/guestContacts';
import UnsavedChangesModal from '../UnsavedChangesModal';
import { toHotelISO } from "../../api/hotelTime";

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Comparable snapshot of the whole modal, used to detect unsaved edits.
const stateSnapshot = (form, guestList) =>
  JSON.stringify({
    checkInDate: form?.checkInDate || "",
    checkOutDate: form?.checkOutDate || "",
    roomNumber: String(form?.roomNumber || ""),
    payment_status: form?.payment_status || "",
    guests: guestList || [],
  });

export default function ManageRoom(props) {
 
  // console.log("Guests Data in Modal : ",props.guestData);
  // console.log("Guests General Data in Modal : ",props.generalData);
  const token = localStorage.getItem("token");
  const [guests,setGuests] = useState([]);
  const [activeGuest,setActiveGuest] = useState(-1);
  const [isFormOpen,setIsFormOpen] = useState(false);
  const [buttonDisabled,setButtonDisabled] = useState(false);
  const [idCopied,setIdCopied] = useState(false);
  const [guestFormDirty,setGuestFormDirty] = useState(false);
  const [unsavedPrompt,setUnsavedPrompt] = useState(false);
  const baselineSnapshot = useRef("");
  // Error Handling for Form
  const [errors,setErrors] = useState({});
  const hotelId = localStorage.getItem('hotelId');

  const copyBookingId = ()=>{
    const bookingId = props?.generalData?.bookingId;
    if(!bookingId){
      return;
    }
    navigator.clipboard.writeText(bookingId).then(()=>{
      setIdCopied(true);
      setTimeout(()=>setIdCopied(false), 2000);
    }).catch((error)=>{
      console.log(error);
      toast.error("Could not copy the booking ID");
    });
  }

  // Contacts already taken by the booking's other rooms. This modal is opened with a
  // single room, so the siblings are pulled once — a contact belongs to one guest per
  // booking, and the backend rejects the save otherwise.
  const [otherRoomContacts,setOtherRoomContacts] = useState(new Set());

  useEffect(()=>{
    const loadOtherRoomContacts = async()=>{
      try{
        const bookingId = props?.generalData?.bookingId;
        if(!bookingId){
          return;
        }
        const response = await axiosInstance.get(
          `/api/v1/hotels/${hotelId}/bookings/${bookingId}/booked-rooms`,{
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setOtherRoomContacts(
          collectOtherRoomContacts(response?.data?.data, props?.generalData?.bookedRoomId)
        );
      }catch(error){
        console.log(error);
      }
    }
    loadOtherRoomContacts();
  },[]);

  // Set The Guests
  useEffect(()=>{
    const initialGuests = props.guestData || [];
    setGuests(initialGuests);
    baselineSnapshot.current = stateSnapshot({
      checkInDate : props?.generalData?.checkInDate || "",
      checkOutDate : props?.generalData?.checkOutDate || "",
      roomNumber :String(props?.generalData?.roomNumber || ""),
      payment_status: "",
    }, initialGuests);
  },[]);

  // General Details of Form
  const [generalForm,setGeneralForm] = useState({
    checkInDate : props?.generalData?.checkInDate || "",
    checkOutDate : props?.generalData?.checkOutDate || "",
    roomNumber :String(props?.generalData?.roomNumber || ""),
    payment_status: "",
  }); 

  const hasUnsavedChanges =
    guestFormDirty || stateSnapshot(generalForm, guests) !== baselineSnapshot.current;

  // A room number is the check-in: reassigning it needs the open requests, the
  // PMS and the guest's welcome details moved with it, none of which this modal
  // does. Assign once here; moves go through a Room Change request.
  const isRoomNumberLocked = Boolean(String(props?.generalData?.roomNumber || "").trim());

  // General Form validate Form for each field
  const validateField = (field, value)=>{
    try{
      const newErrors = { ...errors };
      switch (field) {
        case "checkInDate":
          if (value.trim() === "") {
            newErrors.checkInDate = "check-in-date is required";
          } 
          else{
            newErrors.checkInDate = "";
          }      
          break;
        case "checkOutDate":
          if (value.trim()==="") {
            newErrors.checkOutDate = "check-out-date is required";
          }
          else{
            newErrors.checkOutDate = "";
          }
          break;
        case "roomNumber":
          if (value.trim()==="" || /^\s*$/.test(value) || !/^\d+$/.test(value)) {
            newErrors.roomNumber = "room-number is required with no spaces and only digits allowed";
          }
          else{
            newErrors.roomNumber = "";
          }
          break;

        default:
          break;
      }
    setErrors(newErrors);

    }catch(error){
      console.log(error);
    }
  }

  // validate Form in the End
  const validateForm = ()=>{
    try{
      const newErrors = {};
      if(!generalForm.checkOutDate.trim()){
        newErrors.checkOutDate = "check-out-date is required";
      }
      if(!generalForm.checkInDate.trim()){
        newErrors.checkInDate = "check-in-date is required";
      }
      if (generalForm.roomNumber.trim()==="" || /^\s*$/.test(generalForm.roomNumber) || !/^\d+$/.test(generalForm.roomNumber)) {
        newErrors.roomNumber = "room-number is required with no spaces and only digits allowed";
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;

    }catch(error){
      console.log(error);
    }
  }

  // Handle Change
  const handleChange = (event)=>{
    const name = event.target.name;
    const value = event.target.value;
    setGeneralForm(values => ({...values, [name]: value}));
    validateField(name, value);
  }

  // console.log("Active Guest : " + activeGuest);

  // Close Modal 
  const closeModal = ()=>{
    if(hasUnsavedChanges){
      setUnsavedPrompt(true);
      return;
    }
    props.setModalRoom(false)
  }

  // handle Update The Room
  const handleUpdateRoom = async()=>{
    try{
      if(!validateForm()){
        // console.log("Please Validate Form");
        return;
      }
      if(guests.length===0){
        toast.error("Guests Required");
        return;
      }
      if(guests.every((guest)=>guest.is_minor)){
        toast.error("A room must have at least one adult guest");
        return;
      }
      
      // console.log("General Form : ",generalForm);
      // console.log("Guests : ",guests);
      
      // Assuming generalForm.checkInDate is '2024-12-09T18:30'
      const checkInDate = toHotelISO(generalForm.checkInDate);
      const checkOutDate = toHotelISO(generalForm.checkOutDate);
      const roomNumber = parseInt(generalForm.roomNumber,10);

      // The booked-room payload already carries user_id for every guest linked
      // to an Alfred account, and the backend links the rest by contact — no
      // need to pull down the whole user table on every save.
      const updatedGuests = guests.map(guest => buildGuestPayload(guest));

      // Update the form with the new guests data
      const FinalSubmit = {
        number_of_guests : guests.length,
        checkin_date: checkInDate,
        checkout_date: checkOutDate,
        room_number: roomNumber,
        guest_details: updatedGuests,
        payment_status : generalForm.payment_status
      };
      // console.log("Final Data : " ,FinalSubmit);
      // return
      const response = await axiosInstance.put(
        `/api/v1/hotels/${hotelId}/bookings/${props.generalData.bookingId}/booked-rooms/${props.generalData.bookedRoomId}`,FinalSubmit,{
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // Re-seed from the response instead of re-fetching: it carries the saved
      // guests plus the user_id the backend linked them to.
      const savedGuests = response?.data?.guests || guests;
      setGuests(savedGuests);
      setActiveGuest(-1);
      setIsFormOpen(false);
      baselineSnapshot.current = stateSnapshot(generalForm, savedGuests);

      toast.success("Room Updated Successfully");
 

    }catch(error){
      toast.error(`Error Occured While Updating Rooms : ${error?.response?.data?.message || "Some Error Occured"}`);
      console.log(error);
    }
  }

  // Add guests
  const AddGuests = ()=>{
    try{
      setActiveGuest(-1);
      setIsFormOpen(true);
    }catch(error){
      console.log(error);
    }
  }



  return (
    <>
      <div
        className='fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001] overflow-auto'
        onMouseDown={(e) => {
          // Close only when the click starts on the backdrop itself
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className="manageGuestsModal popup-content bg-white p-6 rounded-xl shadow-2xl text-sm flex flex-col gap-3 w-[80%] md:w-[70%] lg:w-[55%] top-10 max-h-[98vh] overflow-y-auto">

          {/* Header of Manage Room */}
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h2 className="text-xl font-semibold">Manage Room</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Update room details and manage guests
              </p>
            </div>
            <button
              onClick={()=>closeModal()}
              className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
              type="button"
            >
              <X weight="bold" size={18} />
            </button>
          </div>

          {/* Booking reference and stay dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-[#8C9199] mb-2">Booking ID</p>
              <div className="flex items-center gap-2 py-1">
                <span
                  className="font-mono font-semibold tracking-wider text-[#262B33] bg-[#EEF0FB] border-[1.5px] border-[#BFC4CC] rounded-md px-2.5 py-1 truncate"
                  title={props?.generalData?.bookingId || "NA"}
                >
                  {props?.generalData?.bookingId
                    ? `${props.generalData.bookingId.substring(0, 8)}...`
                    : "NA"}
                </span>
                {props?.generalData?.bookingId &&
                  <button
                    type="button"
                    className="text-[#8C9199] hover:text-[#0B22B2] shrink-0"
                    title="Copy booking ID"
                    onClick={()=>copyBookingId()}
                  >
                    {idCopied ? <Checks size={16} /> : <Copy size={16} />}
                  </button>
                }
              </div>
            </div>
            <div>
              <p className="text-[#8C9199] mb-2">Check-in Date</p>
              <input
                type="datetime-local"
                className="w-full p-2 rounded-md border-[1.5px]"
                name="checkInDate"
                value={generalForm.checkInDate}
                onChange={(e)=>handleChange(e)}
              />
              {errors.checkInDate && <span className="text-xs text-[#FF7378]">{errors.checkInDate}</span>}
            </div>
            <div>
              <p className="text-[#8C9199] mb-2">Check-out Date</p>
              <input
                type="datetime-local"
                className="w-full p-2 rounded-md border-[1.5px]"
                name="checkOutDate"
                value={generalForm.checkOutDate}
                onChange={(e)=>handleChange(e)}
              />
              {errors.checkOutDate && <span className="text-xs text-[#FF7378]">{errors.checkOutDate}</span>}
            </div>
          </div>

          {/* Room details — same 3 column grid so both rows line up */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-[#8C9199] mb-2">Room Number</p>
              <input
                type="text"
                placeholder="Enter Room Number"
                className={`w-full p-2 rounded-md border-[1.5px] ${isRoomNumberLocked ? "bg-[#F7F8FC] text-[#8C9199] cursor-not-allowed" : ""}`}
                name="roomNumber"
                value={generalForm.roomNumber}
                onChange={(e)=>handleChange(e)}
                disabled={isRoomNumberLocked}
              />
              {errors.roomNumber && <span className="text-xs text-[#FF7378]">{errors.roomNumber}</span>}
            </div>
            <div>
              <p className="text-[#8C9199] mb-2">Check-in Status</p>
              <div className="w-full p-2 rounded-md border-[1.5px] bg-[#F7F8FC] text-[#8C9199]">
                Completed
              </div>
            </div>
            <div>
              <p className="text-[#8C9199] mb-2">Payment Status</p>
              <select
                className="w-full p-2 rounded-md border-[1.5px]"
                name="payment_status"
                value={generalForm.payment_status}
                onChange={(e)=>handleChange(e)}
              >
                <option value="">Pending</option>
                <option value="SUCCESSFUL">Completed</option>
              </select>
            </div>
          </div>

          {/* Guests Image and Input Fields */}
          <div className="flex gap-2 border-t border-[#BFC4CC]">

            {/* Guests Data */}
            <div className='flex-[0.4] p-2 border-r border-[#BFC4CC] overflow-hidden'>
              <div className="viewGuests flex flex-col gap-2 h-[520px] overflow-y-auto relative p-2">

                {/* Add Guests Button */}
                {!isFormOpen &&
                  <button className={`w-full p-3 bg-[#FF7378] text-white font-medium rounded-lg transition-colors hover:bg-[#f0575d] ${buttonDisabled ? "bg-[#FFF2F2] text-[#FF7378] cursor-not-allowed hover:bg-[#FFF2F2]" : ""}`}
                    onClick={()=>AddGuests()}
                    disabled={buttonDisabled}
                  >
                    + Add Guest
                  </button>
                }

                {guests.map((guest, index) => {
                  return <ManageRoomGuest guest={guest} key={index} guestIndex={index}
                    activeGuest={activeGuest}
                    setActiveGuest={setActiveGuest}
                    guests = {guests}
                    setGuests = {setGuests}
                    buttonDisabled = {buttonDisabled}
                    setButtonDisabled = {setButtonDisabled}
                    isFormOpen = {isFormOpen}
                    setIsFormOpen = {setIsFormOpen}
                  />
                })}
              </div>
            </div>

            {/* Guest Input Field */}
            <div className='flex-[0.6]'>
              {!isFormOpen &&
                <div className="h-[520px] flex flex-col items-center justify-center gap-2 text-center px-6 text-[#8C9199]">
                  <UserPlus size={32} />
                  <p className="font-medium text-[#262B33]">No guest selected</p>
                  <p className="text-xs">
                    Pick a guest on the left to edit them, or use “+ Add Guest” to add a new one.
                  </p>
                </div>
              }
              <ManageRoomGuestFields
                guests = {guests}
                activeGuest = {activeGuest}
                setActiveGuest = {setActiveGuest}
                guestData = {guests[activeGuest]}
                setGuests = {setGuests}
                buttonDisabled = {buttonDisabled}
                setButtonDisabled = {setButtonDisabled}
                isFormOpen = {isFormOpen}
                setIsFormOpen = {setIsFormOpen}
                setGuestFormDirty = {setGuestFormDirty}
                otherRoomContacts = {otherRoomContacts}
              />
            </div>

          </div>

          {/* Submit Button For Room */}
          <div className='flex flex-col gap-4 mt-2 text-center'>
            <button className={`w-full bg-[#FF7378] text-white font-semibold p-2.5 rounded-lg transition-colors hover:bg-[#f0575d] ${buttonDisabled ? "bg-[#FFF2F2] text-[#FF7378] cursor-not-allowed hover:bg-[#FFF2F2]" : ""}`}
              onClick={()=>handleUpdateRoom()}
              disabled={buttonDisabled}
            >
              Update Room
            </button>
          </div>

        </div>
      </div>

      <Toaster
        position="bottom-right"
        reverseOrder={false}
        transition="Bounce"
        style={{ marginBottom: "25%" }}
      />

      {unsavedPrompt &&
        <UnsavedChangesModal
          message="Please save the changes of your room by clicking on Update Room."
          onDismiss={()=>setUnsavedPrompt(false)}
          onDiscard={()=>{
            setUnsavedPrompt(false);
            props.setModalRoom(false);
          }}
        />
      }
    </>
  )
}
